import { Caption1, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import {
  Apps20Regular,
  Briefcase20Regular,
  Building20Regular,
  Checkmark12Filled,
  Code20Regular,
  Color20Regular,
  Flash20Regular,
  Folder20Regular,
  Globe20Regular,
  Grid20Regular,
  Heart20Regular,
  Layer20Regular,
  Megaphone20Regular,
  Phone20Regular,
  Rocket20Regular,
  Sparkle20Regular,
  Target20Regular,
  type FluentIcon,
} from "@fluentui/react-icons";
import { useId, type KeyboardEvent } from "react";

export const TEAM_ICONS: Array<{ id: string; label: string; Icon: FluentIcon }> = [
  { id: "globe", label: "지구", Icon: Globe20Regular },
  { id: "phone", label: "전화", Icon: Phone20Regular },
  { id: "megaphone", label: "메가폰", Icon: Megaphone20Regular },
  { id: "rocket", label: "로켓", Icon: Rocket20Regular },
  { id: "apps", label: "앱", Icon: Apps20Regular },
  { id: "building", label: "건물", Icon: Building20Regular },
  { id: "grid", label: "격자", Icon: Grid20Regular },
  { id: "palette", label: "팔레트", Icon: Color20Regular },
  { id: "code", label: "코드", Icon: Code20Regular },
  { id: "briefcase", label: "서류가방", Icon: Briefcase20Regular },
  { id: "target", label: "과녁", Icon: Target20Regular },
  { id: "layers", label: "레이어", Icon: Layer20Regular },
  { id: "flash", label: "번개", Icon: Flash20Regular },
  { id: "heart", label: "하트", Icon: Heart20Regular },
  { id: "folder", label: "폴더", Icon: Folder20Regular },
  { id: "sparkle", label: "반짝임", Icon: Sparkle20Regular },
];

/** Eight preset swatches. Selected state is a check and a same-color ring. */
export const TEAM_COLOR_PRESETS: Array<{ value: string; label: string }> = [
  { value: "#3b6fe0", label: "블루" },
  { value: "#4b8fe8", label: "스카이" },
  { value: "#8e6bb8", label: "퍼플" },
  { value: "#1aa394", label: "틸" },
  { value: "#e15b86", label: "핑크" },
  { value: "#c6a04a", label: "골드" },
  { value: "#3e9a5c", label: "그린" },
  { value: "#737880", label: "그레이" },
];

export const DEFAULT_TEAM_ICON = TEAM_ICONS[0]!.id;
export const DEFAULT_TEAM_COLOR = TEAM_COLOR_PRESETS[0]!.value;

const ICON_COLUMNS = 8;
const COLOR_COLUMNS = 4;

const useStyles = makeStyles({
  row: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: "16px",
    flexWrap: "wrap",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    minWidth: 0,
  },
  label: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
  },
  iconGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(8, 30px)",
    columnGap: "6px",
    rowGap: "6px",
  },
  iconButton: {
    width: "30px",
    height: "30px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    border: "1px solid transparent",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground2,
    cursor: "pointer",
    transitionProperty: "background-color, color, border-color, box-shadow",
    transitionDuration: tokens.durationFaster,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  iconButtonSelected: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    boxShadow: `0 0 0 1px ${tokens.colorNeutralStroke1}`,
  },
  colorGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 24px)",
    columnGap: "10px",
    rowGap: "8px",
    padding: "2px",
    flexShrink: 0,
  },
  swatch: {
    width: "24px",
    height: "24px",
    borderRadius: tokens.borderRadiusCircular,
    border: "none",
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transitionProperty: "transform, box-shadow",
    transitionDuration: tokens.durationFaster,
    ":hover": {
      transform: "scale(1.1)",
    },
    ":active": { opacity: 0.88, transform: "scale(0.96)" },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "3px",
    },
  },
  mark: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  dot: {
    width: "8px",
    height: "8px",
    borderRadius: tokens.borderRadiusCircular,
    flexShrink: 0,
  },
});

export function teamIconById(id: string | undefined) {
  return TEAM_ICONS.find((item) => item.id === id);
}

function inkOn(hex: string): string {
  const n = Number.parseInt(hex.replace("#", ""), 16);
  if (!Number.isFinite(n)) return "#fff";
  const y = (((n >> 16) & 255) * 299 + ((n >> 8) & 255) * 587 + (n & 255) * 114) / 1000;
  return y > 150 ? "#1b1b1b" : "#fff";
}

function moveInGrid(event: KeyboardEvent<HTMLButtonElement>, index: number, columns: number, count: number, pick: (index: number) => void) {
  const delta =
    event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : event.key === "ArrowDown" ? columns : event.key === "ArrowUp" ? -columns : 0;
  if (!delta) return;
  event.preventDefault();
  const next = index + delta;
  if (next < 0 || next >= count) return;
  pick(next);
  const buttons = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
  buttons?.[next]?.focus();
}

/** Icon grid and preset colors for a team, side by side. */
export function TeamAppearanceFields({
  icon,
  color,
  onIconChange,
  onColorChange,
}: {
  icon: string;
  color: string;
  onIconChange: (id: string) => void;
  onColorChange: (hex: string) => void;
}) {
  const styles = useStyles();
  const iconLabelId = useId();
  const colorLabelId = useId();
  const colorOn = color.toLowerCase();
  const colorChosen = TEAM_COLOR_PRESETS.some((item) => item.value === colorOn);

  return (
    <div className={styles.row}>
      <div className={styles.section}>
        <Caption1 id={iconLabelId} className={styles.label}>
          아이콘
        </Caption1>
        <div className={styles.iconGrid} role="radiogroup" aria-labelledby={iconLabelId}>
          {TEAM_ICONS.map((item, index) => {
            const selected = item.id === icon;
            const Icon = item.Icon;
            return (
              <button
                key={item.id}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={item.label}
                title={item.label}
                tabIndex={selected ? 0 : -1}
                className={mergeClasses(styles.iconButton, selected && styles.iconButtonSelected)}
                style={selected ? { color } : undefined}
                onClick={() => onIconChange(item.id)}
                onKeyDown={(event) => moveInGrid(event, index, ICON_COLUMNS, TEAM_ICONS.length, (next) => onIconChange(TEAM_ICONS[next]!.id))}
              >
                <Icon />
              </button>
            );
          })}
        </div>
      </div>
      <div className={styles.section}>
        <Caption1 id={colorLabelId} className={styles.label}>
          컬러
        </Caption1>
        <div className={styles.colorGrid} role="radiogroup" aria-labelledby={colorLabelId}>
          {TEAM_COLOR_PRESETS.map((item, index) => {
            const selected = item.value === colorOn;
            return (
              <button
                key={item.value}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={item.label}
                title={item.label}
                tabIndex={selected || (!colorChosen && index === 0) ? 0 : -1}
                className={styles.swatch}
                style={{
                  backgroundColor: item.value,
                  color: inkOn(item.value),
                  boxShadow: selected
                    ? `0 0 0 2px ${tokens.colorNeutralBackground1}, 0 0 0 4px ${item.value}`
                    : "inset 0 0 0 1px rgba(0, 0, 0, 0.12)",
                }}
                onClick={() => onColorChange(item.value)}
                onKeyDown={(event) => moveInGrid(event, index, COLOR_COLUMNS, TEAM_COLOR_PRESETS.length, (next) => onColorChange(TEAM_COLOR_PRESETS[next]!.value))}
              >
                {selected ? <Checkmark12Filled /> : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Team icon in its color. Teams without an icon keep a color dot. */
export function TeamMark({ icon, color, size = 16 }: { icon?: string; color?: string; size?: number }) {
  const styles = useStyles();
  const entry = teamIconById(icon);
  if (entry) {
    const Icon = entry.Icon;
    return <Icon className={styles.mark} style={{ width: size, height: size, color: color || tokens.colorNeutralForeground3 }} aria-hidden />;
  }
  if (!color) return null;
  return <span className={styles.dot} style={{ backgroundColor: color }} aria-hidden />;
}
